import Joi from 'joi';
import PropTypes from 'prop-types';

import Conditional from '../../common/conditional';
import Form from '../../common/form';

import { getFileUploadTypes, getPCNLRiskFactorClassification } from '../../../utils/utilities';
import { ANOMALY_INTERVENTION_TYPES, RISK_RATING_OPTIONS } from '../../../utils/constants';
import { CoverDataItem } from '../../../utils/models';

import toastService from '../../../services/toastService';

class PCNLAnomalyForm extends Form {
	state = {
		data: {
			rootCause: '',
			location: '',
			description: [],
			recommendation: [],
			riskFactor: '',
			interventionRequired: '',
			generalImage: [],
			closeImage: [],
		},
		errors: {},
		loading: false,
		progress: 0,
	};

	schema = {
		rootCause: Joi.string().min(5).required().label('Root Cause'),
		location: Joi.string().min(3).required().label('Anomaly Location'),
		description: Joi.array()
			.min(1)
			.items(
				Joi.object({
					label: Joi.string().required(),
					value: Joi.string().required(),
					__isNew__: Joi.boolean().optional(),
				})
			)
			.required()
			.label('Anomaly / Inspection Description'),
		recommendation: Joi.array()
			.min(1)
			.items(
				Joi.object({
					label: Joi.string().required(),
					value: Joi.string().required(),
					__isNew__: Joi.boolean().optional(),
				})
			)
			.required()
			.label('Remark & Recommendation'),
		riskFactor: Joi.string().valid(...RISK_RATING_OPTIONS).required().label('Risk Factor'),
		interventionRequired: Joi.object({
			label: Joi.string().required(),
			value: Joi.string().required(),
			__isNew__: Joi.boolean().optional(),
		}).required().label('Intervention Required'),
		generalImage: Joi.array().min(1).max(1).required().label('General Image'),
		closeImage: Joi.array().min(1).max(1).required().label('Close Image'),
	};

	static propTypes = {
		onAdd: PropTypes.func,
	};
	
	getData = () => {
		const data = { ...this.state.data };
		data.interventionRequired = (data.interventionRequired as unknown as CoverDataItem).value;

		return data;
	};

	doSubmit = (): void => {
		this.props.onAdd(this.getData());

		this.setState({ 
			data: {
				rootCause: '',
				location: '',
				description: [],
				recommendation: [],
				riskFactor: '',
				interventionRequired: '',
				generalImage: [],
				closeImage: [],
			}
		});

		toastService.success('Anomaly Item Saved Successfully!');
	};
	
	getSelectedClass = () => {
		return getPCNLRiskFactorClassification(this.state.data.riskFactor);
	};

	getSelectedClassDescription = () => {
		const selectedClass = this.getSelectedClass();
		if (selectedClass) return selectedClass.descriptions;

		return [];
	};
	
	getSelectedClassRecommendations = () => {
		const selectedClass = this.getSelectedClass();
		if (selectedClass) return selectedClass.recommendations;

		return [];
	};

	render() {
		return (
			<form autoComplete="off" onSubmit={this.handleSubmit}>
				<div className="row">
					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'location',
							placeholder: 'Enter Anomaly Location',
							label: 'Anomaly Location',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'rootCause',
							placeholder: 'Enter Root Cause',
							label: 'Root Cause',
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'riskFactor',
							placeholder: 'Select Risk Factor',
							label: 'Risk Factor',
							options: RISK_RATING_OPTIONS.map((option) => ({ id: option, name: option })),
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					<Conditional isVisible={!!this.getSelectedClass()}>
						<div className="col">
							{this.renderCreatableMultiselect({
								closeMenuOnSelect: true,
								options: this.getSelectedClassDescription(),
								name: 'description',
								placeholder: 'Enter Anomaly Description',
								label: 'Anomaly Description',
								isMulti: true,
							})}
						</div>

						<div className="col">
							{this.renderCreatableMultiselect({
								closeMenuOnSelect: true,
								options: this.getSelectedClassRecommendations(),
								name: 'recommendation',
								placeholder: 'Enter Remark & Recommendation',
								label: 'Remark & Recommendation',
								isMulti: true,
							})}
						</div>
					</Conditional>

					<div className="col">
						{this.renderCreatableMultiselect({
							closeMenuOnSelect: true,
							options: ANOMALY_INTERVENTION_TYPES,
							name: 'interventionRequired',
							placeholder: 'Enter Intervention Required (Optional)',
							label: 'Intervention Required (Optional)',
							isMulti: false,
						})}
					</div>
				</div>

				<div className="divider my-4"></div>
				
				<div className="row">
					<div className="col">
						{this.renderFileInput({
							max: 1,
							multiple: false,
							name: 'generalImage',
							accept: 'image/*,application/pdf',
							label: 'Anomaly Image (General View)',
							sublabel: 'Upload photos showing general view',
							types: getFileUploadTypes(),
						})}
					</div>

					<div className="col">
						{this.renderFileInput({
							max: 1,
							multiple: false,
							name: 'closeImage',
							accept: 'image/*,application/pdf',
							label: 'Anomaly Image (Close View)',
							sublabel: 'Upload photos showing close view',
							types: getFileUploadTypes(),
						})}
					</div>
				</div>

				<div className="divider mt-4"></div>

				<div className="d-flex justify-content-end">
					{this.renderButton('Add Anomaly Item')}
				</div>
			</form>
		);
	}
}

export default PCNLAnomalyForm;
